'use client';

import React, { useState } from 'react';
import { Flex, Box, Text, HStack, VStack, IconButton } from '@skin-seoul/design-system';
import LucideIcon from '@/components/ui/LucideIcon';
import { StarIcon } from '@/assets/StarIcon';
import type { Currency } from '@skin-seoul/service';
import { CLASSNAME, ProductType, formatCurrency, useProductService, useRegionCurrency, productSizeAndPackTextConverter } from '@skin-seoul/service';
import { useSafeRouter } from '@/hooks';
import { unescape } from 'es-toolkit/string';
import { useParams } from 'next/navigation';
import { REVIEW_SECTION_ID } from '@/containers/product-detail/sections/ReviewSection/ReviewSection';
interface ProductBasicInfoProps {
  onShare: () => void;
  selectedOption: string;
}
const ProductBasicInfo = ({
  onShare,
  selectedOption
}: ProductBasicInfoProps) => {
  const {
    useGetProductsDetail
  } = useProductService();
  const {
    currency
  } = useRegionCurrency();
  const params = useParams();
  const {
    data: {
      product,
      relatedCollections,
      categories,
      reviewStatic
    }
  } = useGetProductsDetail({
    slug: params.slug as string,
    currency
  });
  const router = useSafeRouter();
  const [showCopyIcon, setShowCopyIcon] = useState(false);
  const getPrice = () => {
    if (product.type === ProductType.Variable) {
      const targetOption = selectedOption || product.variations[0]?.productId;
      const selectedVariation = product.variations.find(variation => variation.productId === targetOption);
      if (selectedVariation) {
        return {
          originalPrice: selectedVariation.originalPrice,
          salePrice: selectedVariation.salePrice
        };
      }
      const firstVariation = product.variations[0];
      return {
        originalPrice: firstVariation?.originalPrice,
        salePrice: firstVariation?.salePrice
      };
    }
    return {
      originalPrice: product.originalPrice,
      salePrice: product.salePrice
    };
  };
  const {
    originalPrice,
    salePrice
  } = getPrice();
  const discountPercentage = salePrice && originalPrice && salePrice < originalPrice ? Math.round((originalPrice - salePrice) / originalPrice * 100) : 0;
  const handleSkuClick = async (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    e.preventDefault();
    try {
      await navigator.clipboard.writeText(product.sku);
      // Icon display commented out.
      // setShowCopyIcon(true);

      setTimeout(() => {
        setShowCopyIcon(false);
      }, 2000);
    } catch (error) {
      console.error('Failed to copy SKU to clipboard:', error);
    }
  };
  const handleReviewClick = () => {
    const reviewSection = document.getElementById(REVIEW_SECTION_ID);
    reviewSection?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const productSizeAndPackText = productSizeAndPackTextConverter({
    size: product.size,
    pack: product.pack,
    color: product.color
  });
  return <Box width="100%" background="white" borderBottom="1px solid" borderColor="neutral.50" paddingY={{
    base: 5,
    tablet: 6
  }} paddingX={{
    base: 4,
    tablet: 4
  }} data-sentry-element="Box" data-sentry-component="ProductBasicInfo" data-sentry-source-file="ProductBasicInfo.tsx">
      <VStack gap={{
      base: 3,
      tablet: 6
    }} width="100%" alignItems="flex-start" data-sentry-element="VStack" data-sentry-source-file="ProductBasicInfo.tsx">
        {/* brand and sku */}
        <VStack gap={4} width="100%" alignItems="flex-start" data-sentry-element="VStack" data-sentry-source-file="ProductBasicInfo.tsx">
          <Flex width="100%" justifyContent="space-between" alignItems="center" data-sentry-element="Flex" data-sentry-source-file="ProductBasicInfo.tsx">
            <HStack gap={2} alignItems="center" minW="0" flex="1" data-sentry-element="HStack" data-sentry-source-file="ProductBasicInfo.tsx">
              <Text textStyle={{
              base: 'label.large',
              tablet: 'heading.h6'
            }} color="neutral.700" fontWeight="semibold" letterSpacing={{
              tablet: '-0.4px'
            }} cursor="pointer" onClick={() => {
              router.push(`/brand/${relatedCollections?.brand?.slug || ''}`);
            }} _hover={{
              opacity: 0.8
            }} transition="opacity 0.2s" truncate minW="0" flexShrink={1} display="flex" alignItems="center" height="28px" className={CLASSNAME.NOT_TRANSLATE} data-sentry-element="Text" data-sentry-source-file="ProductBasicInfo.tsx">
                {relatedCollections?.brand?.name}
              </Text>
              <Text textStyle={{
              base: 'label.medium',
              tablet: 'label.large'
            }} color="primary.400" fontWeight="medium" cursor="pointer" truncate minW="0" display="flex" alignItems="center" flex={1} flexShrink={2} height="28px" onClick={() => {
              router.push(`/collections/${categories[0]?.slug || ''}`);
            }} className={CLASSNAME.NOT_TRANSLATE} data-sentry-element="Text" data-sentry-source-file="ProductBasicInfo.tsx">
                {unescape(categories[0]?.name || '')}
              </Text>
            </HStack>

            <HStack gap={1} alignItems="center" cursor="pointer" width={{
            base: '150px',
            tablet: '150px'
          }} justifyContent="flex-end" flexShrink={0} display={{
            base: 'none',
            tablet: 'flex'
          }} data-sentry-element="HStack" data-sentry-source-file="ProductBasicInfo.tsx">
              {showCopyIcon && <LucideIcon name="CopyCheck" size="sm" color="neutral.400" />}
              <Text onClick={handleSkuClick} textStyle={{
              base: 'label.medium',
              tablet: 'label.large'
            }} color="neutral.400" textDecoration="none" WebkitTouchCallout="none" display="flex" alignItems="center" justifyContent="flex-end" as="span" width="100%" textAlign="right" truncate height="28px" className={CLASSNAME.NOT_TRANSLATE} data-sentry-element="Text" data-sentry-source-file="ProductBasicInfo.tsx">
                SKU {product.sku}
              </Text>
            </HStack>
          </Flex>
          <VStack gap={3} alignItems="flex-start" data-sentry-element="VStack" data-sentry-source-file="ProductBasicInfo.tsx">
            <Text textStyle={{
            base: 'heading.h5.mobile',
            tablet: 'heading.h5'
          }} color="neutral.800" fontWeight="medium" width="100%" letterSpacing={{
            tablet: '-0.48px'
          }} className={CLASSNAME.NOT_TRANSLATE} data-sentry-element="Text" data-sentry-source-file="ProductBasicInfo.tsx">
              {product.name}
            </Text>
            {/* TODO: size and pack are actually required fields, but this is a temporary workaround */}
            {product.type === ProductType.Simple && productSizeAndPackText && <Text textStyle="label.large" className={CLASSNAME.NOT_TRANSLATE}>
                {productSizeAndPackText}
              </Text>}
          </VStack>
        </VStack>

        <VStack gap={4} width="100%" alignItems="flex-start" data-sentry-element="VStack" data-sentry-source-file="ProductBasicInfo.tsx">
          {/* review */}
          {reviewStatic.totalReviews > 0 && <Flex width="100%" justifyContent="flex-start" alignItems="center">
              <HStack gap={2} alignItems="center" onClick={handleReviewClick}>
                <HStack gap={1} alignItems="center">
                  <StarIcon />
                  <Text textStyle={{
                base: 'label.medium',
                tablet: 'label.large'
              }} color="neutral.700" fontWeight="medium">
                    {reviewStatic.averageRate}
                  </Text>
                </HStack>
                <Text textStyle={{
              base: 'label.medium',
              tablet: 'label.large'
            }} color="neutral.400" fontWeight={{
              tablet: 'medium'
            }}>
                  ({reviewStatic.totalReviews.toLocaleString()})
                </Text>
              </HStack>
            </Flex>}

          {/* price */}
          {product.status === 'outofstock' ? <Flex width="100%" justifyContent="space-between" alignItems="center">
              <Flex backgroundColor="error.50" borderRadius="4px" paddingX="8px" paddingY="4px" alignItems="center" gap="4px">
                <Text textStyle={{
              base: 'label.medium',
              tablet: 'label.medium'
            }} color="error.600" fontWeight="medium">
                  <LucideIcon name="CircleAlert" size="sm" marginBottom="2px" /> Temporarily out of stock
                </Text>
              </Flex>

              <IconButton aria-label="Share" onClick={onShare} backgroundColor="neutral.50" borderRadius="8px" width="48px" height="48px" color="neutral.700" _hover={{
            backgroundColor: 'neutral.100'
          }}>
                <LucideIcon name="Share2" strokeWidth={1.2} />
              </IconButton>
            </Flex> : <Flex width="100%" justifyContent="space-between" alignItems="center">
              <HStack gap={2} alignItems="center">
                <HStack gap={3} alignItems="center">
                  <Text textStyle={{
                base: 'heading.h5.mobile',
                tablet: 'heading.h5'
              }} color="neutral.800" fontWeight={{
                base: 'medium',
                tablet: 'semibold'
              }} letterSpacing={{
                tablet: '-0.48px'
              }} className={CLASSNAME.NOT_TRANSLATE}>
                    {formatCurrency(product.currency as Currency, salePrice ?? originalPrice ?? 0)}
                  </Text>
                  {salePrice && originalPrice && salePrice < originalPrice && <Text textStyle={{
                base: 'label.large',
                tablet: 'heading.h6'
              }} color="neutral.400" textDecoration="line-through" letterSpacing={{
                tablet: '-0.4px'
              }} className={CLASSNAME.NOT_TRANSLATE}>
                      {formatCurrency(product.currency as Currency, originalPrice)}
                    </Text>}
                </HStack>
                {salePrice && originalPrice && salePrice < originalPrice && <Text textStyle={{
              base: 'label.medium',
              tablet: 'label.large'
            }} color="error.600" fontWeight="medium" className={CLASSNAME.NOT_TRANSLATE}>
                    {discountPercentage}% OFF
                  </Text>}
              </HStack>
              <IconButton aria-label="Share" onClick={onShare} backgroundColor="neutral.50" borderRadius="8px" width="48px" height="48px" color="neutral.700" _hover={{
            backgroundColor: 'neutral.100'
          }}>
                <LucideIcon name="Share2" strokeWidth={1.2} />
              </IconButton>
            </Flex>}
        </VStack>
      </VStack>
    </Box>;
};
export default ProductBasicInfo;